<div class="facility-groups-container">
  <div class="page-header">
    <div class="header-label">Organization Settings</div>
    <h1>Facility Groups</h1>
    <p class="description">
      Organize facilities into groups for filtering and reporting. Each facility
      can belong to one group at a time.
    </p>

    <!-- Apply in census toggle -->
    <div class="census-toggle">
      <input
        type="checkbox"
        id="applyInCensus"
        [(ngModel)]="applyInCensus"
        (change)="toggleCensusApply()"
      />
      <label for="applyInCensus" class="toggle-label">
        <span class="toggle-title">Apply in census</span>
        <span class="toggle-desc">
          When on, the census report groups facilities by group and shows a
          subtotal row per group. Ungrouped facilities appear under "Other".
        </span>
      </label>
    </div>
  </div>

  <!-- Main card -->
  <div class="card">
    <!-- Filter chip strip -->
    <div class="filter-strip">
      <button
        class="filter-chip"
        [class.active]="filterGroupId === 'unassigned'"
        (click)="onFilterChange('unassigned')"
      >
        Ungrouped (Other)
        <span class="count">{{ unassignedCount }}</span>
      </button>

      <div
        *ngIf="groups.length > 0"
        class="divider"
      ></div>

      <ng-container *ngFor="let group of groups">
        <button
          class="group-chip"
          [class.active]="filterGroupId === group._id"
          (click)="onFilterChange(group._id)"
          (contextmenu)="$event.preventDefault(); onChipMenuOpen(group._id)"
        >
          <span class="chip-text">{{ group.name }}</span>
          <span class="count">{{ facilityCountByGroup[group._id] || 0 }}</span>
          <button
            class="menu-btn"
            (click)="$event.stopPropagation(); onChipMenuOpen(group._id)"
          >
            ⋯
          </button>

          <!-- Group menu -->
          <div *ngIf="chipMenuFor === group._id" class="menu">
            <button (click)="openModal('edit', group); chipMenuFor = null" class="menu-item">
              ✏️ Edit group
            </button>
            <button (click)="openModal('deleteGroup', group); chipMenuFor = null" class="menu-item danger">
              🗑️ Delete group
            </button>
          </div>
        </button>
      </ng-container>

      <button class="new-group-btn" (click)="openModal('create')">
        + New group
      </button>
    </div>

    <!-- Empty state -->
    <div *ngIf="groups.length === 0 && hydrated" class="empty-state">
      <span class="step">1</span>
      <span>
        <strong>Create your first group</strong>
        <span class="muted">
          — then assign facilities to it using each row's menu, or select multiple and assign in bulk.
        </span>
      </span>
    </div>

    <!-- Toolbar -->
    <div class="toolbar" *ngIf="visibleFacilities.length > 0 || selectedCount > 0">
      <input
        type="text"
        placeholder="Search facilities..."
        [(ngModel)]="search"
        (input)="onSearch(search)"
        class="search-input"
      />

      <ng-container *ngIf="selectedCount > 0">
        <span class="selected-count">{{ selectedCount }} selected</span>

        <ng-container *ngIf="focusedGroup">
          <button
            class="btn btn-primary"
            (click)="openModal('confirmBulkAssign', { targetGroup: focusedGroup })"
          >
            Assign to {{ focusedGroup.name }}
          </button>
        </ng-container>

        <ng-container *ngIf="filterGroupId === 'unassigned'">
          <button
            class="btn btn-primary"
            [disabled]="groups.length === 0"
            (click)="openModal('bulkApply', 'assign')"
          >
            Assign to group…
          </button>
        </ng-container>

        <button class="btn btn-secondary" (click)="openModal('confirmBulkUnassign')">
          Unassign
        </button>
      </ng-container>
    </div>

    <!-- Column header -->
    <div *ngIf="visibleFacilities.length > 0" class="column-header">
      <input
        type="checkbox"
        [checked]="allVisibleSelected"
        (change)="toggleAllVisible()"
      />
      <span>Facility</span>
      <span>Group</span>
    </div>

    <!-- Facility rows -->
    <div *ngIf="visibleFacilities.length > 0" class="facility-list-container">
      <div class="facility-list">
        <ng-container *ngFor="let facility of visibleFacilities">
          <div class="facility-row">
            <input
              type="checkbox"
              [checked]="selectedFacilityIds.has(facility.facilityId)"
              (change)="toggleFacility(facility.facilityId)"
            />
            <span class="facility-name">{{ getFacilityDisplayName(facility) }}</span>
          <div class="group-cell">
            <button
              class="group-btn"
              [class.assigned]="facility.currentGroup"
              (click)="onRowMenuOpen(facility.facilityId)"
            >
              {{ facility.currentGroup?.groupName || 'Assign group' }}
              <span class="chevron">▼</span>
            </button>

            <!-- Row menu -->
            <div *ngIf="rowMenuFor === facility.facilityId" class="row-menu">
              <div class="menu-label">Assign to group</div>

              <ng-container *ngIf="groups.length === 0">
                <div class="menu-empty">No groups yet.</div>
              </ng-container>

              <ng-container *ngFor="let group of groups">
                <button
                  class="menu-item"
                  [class.active]="facility.currentGroup?.groupId === group._id"
                  (click)="setFacilityGroup(facility.facilityId, group._id); rowMenuFor = null"
                >
                  <span>{{ group.name }}</span>
                </button>
              </ng-container>

              <div class="menu-divider"></div>

              <button
                class="menu-item"
                (click)="openModal('createAndAssign', facility); rowMenuFor = null"
              >
                + Create new group…
              </button>

              <ng-container *ngIf="facility.currentGroup">
                <button
                  class="menu-item danger"
                  (click)="setFacilityGroup(facility.facilityId, null); rowMenuFor = null"
                >
                  Unassign
                </button>
              </ng-container>
            </div>
          </div>
        </div>
      </ng-container>
      </div>
    </div>

    <!-- No results -->
    <div *ngIf="visibleFacilities.length === 0 && hydrated" class="no-results">
      {{ search.trim()
        ? 'No facilities match that search.'
        : 'Every facility is in a group.' }}
    </div>
  </div>
</div>

<!-- Modals -->

<!-- Create/Edit Group Modal -->
<div
  *ngIf="modal?.type === 'create' || modal?.type === 'edit'"
  class="modal-overlay"
  (click)="closeModal()"
>
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>{{ modal.type === 'create' ? 'New group' : 'Edit group' }}</h2>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label>Group name <span class="required">*</span></label>
        <input
          type="text"
          [(ngModel)]="groupForm.name"
          placeholder="e.g. Pacific Northwest"
          class="form-input"
        />
      </div>
      <div class="form-group">
        <label>Description</label>
        <textarea
          [(ngModel)]="groupForm.description"
          placeholder="Optional notes about this group"
          class="form-input"
          rows="3"
        ></textarea>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
      <button
        class="btn btn-primary"
        [disabled]="!groupForm.name?.trim()"
        (click)="
          modal.type === 'create'
            ? createGroup(groupForm)
            : updateGroup(modal.payload._id, groupForm)
        "
      >
        {{ modal.type === 'create' ? 'Create group' : 'Save changes' }}
      </button>
    </div>
  </div>
</div>

<!-- Delete Group Modal -->
<div
  *ngIf="modal?.type === 'deleteGroup'"
  class="modal-overlay"
  (click)="closeModal()"
>
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>Delete group?</h2>
    </div>
    <div class="modal-body">
      <strong>{{ modal.payload.name }}</strong> will be deleted.
      <span *ngIf="(facilityCountByGroup[modal.payload._id] || 0) > 0">
        The {{ facilityCountByGroup[modal.payload._id] }}
        {{ facilityCountByGroup[modal.payload._id] === 1 ? 'facility' : 'facilities' }}
        currently in this group will become unassigned.
      </span>
      <span *ngIf="(facilityCountByGroup[modal.payload._id] || 0) === 0">
        This group has no facilities in it.
      </span>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
      <button class="btn btn-danger" (click)="deleteGroup(modal.payload._id)">
        Delete group
      </button>
    </div>
  </div>
</div>

<!-- Bulk Apply Modal -->
<div
  *ngIf="modal?.type === 'bulkApply'"
  class="modal-overlay"
  (click)="closeModal()"
>
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>Assign {{ selectedCount }} {{ selectedCount === 1 ? 'facility' : 'facilities' }} to a group</h2>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label>Group</label>
        <select [(ngModel)]="bulkGroupId" class="form-input">
          <option *ngFor="let group of groups" [value]="group._id">
            {{ group.name }}
          </option>
        </select>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
      <button
        class="btn btn-primary"
        [disabled]="!bulkGroupId"
        (click)="bulkApply(bulkGroupId)"
      >
        Assign
      </button>
    </div>
  </div>
</div>

<!-- Confirm Bulk Assign Modal -->
<div
  *ngIf="modal?.type === 'confirmBulkAssign'"
  class="modal-overlay"
  (click)="closeModal()"
>
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>
        Assign {{ selectedCount }}
        {{ selectedCount === 1 ? 'facility' : 'facilities' }} to
        {{ modal.payload.targetGroup.name }}?
      </h2>
    </div>
    <div class="modal-body">
      These facilities will be assigned to
      <strong>{{ modal.payload.targetGroup.name }}</strong>.
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
      <button class="btn btn-primary" (click)="bulkApply(modal.payload.targetGroup._id)">
        Assign
      </button>
    </div>
  </div>
</div>

<!-- Confirm Bulk Unassign Modal -->
<div
  *ngIf="modal?.type === 'confirmBulkUnassign'"
  class="modal-overlay"
  (click)="closeModal()"
>
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>
        Unassign {{ selectedCount }} {{ selectedCount === 1 ? 'facility' : 'facilities' }}?
      </h2>
    </div>
    <div class="modal-body">
      These facilities will be unassigned from their groups.
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeModal()">Cancel</button>
      <button class="btn btn-danger" (click)="bulkApply(null)">
        Unassign
      </button>
    </div>
  </div>
</div>
